import React, { useMemo, useEffect, useState } from "react";

import "./WSPortfolioItem.scss";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import { IconButton, Tooltip } from "@mui/material";
import { connect } from "react-redux";
import BeautifulSkeleton from "../../../common/Skeletons/BeautifulSkeleton";
import { dummyOptions } from "../../../TradingView/helpers";
import SparkLines from "../../../common/SparkLines";
import { useHistory, useLocation } from "react-router-dom";
import { init_portfolio } from "../../../../appRedux/ducks/wealthPortfolio";
import moment from "moment";
import FallbackTickerIcon from "../../../common/FallbackTickerIcon/FallbackTickerIcon";
import { portfolioSubscriptionRequest } from "../../../../appRedux/ducks/portfolioSubscription";
import CssPop from "../../../common/Tables/animations/CssPop";
import { useAuth } from "../../../../contexts/Auth";
import { useParams } from "react-router-dom";
const lookup = {
  "Capital Jetstream": {
    number: 25,
    count: 25,
  },
  "Safe Harbor": {
    number: 25,
    count: 25,
  },
  "Robust Anchor": {
    number: 40,
    count: 40,
  },
};
const WSPortfolioItem = ({
  portfolio = {},
  type = "",
  wealthPortfolios = {},
  loadingPortfolio = false,
  handleSubscription = () => {},
  subscribedPortfolios = [],
  loadingSub = false,
}) => {
  const location = useLocation();
  const { series } = useParams();
  const { isBlurred } = useAuth();
  const history = useHistory();
  const portfolioName = portfolio.url || "";
  const portfolioBaseName = portfolioName.replace("_auto", "");

  // Helper function to get portfolio data considering both regular and auto versions
  const getPortfolioData = useMemo(() => {
    // First try the regular portfolio name
    if (wealthPortfolios[portfolioBaseName]) {
      return wealthPortfolios[portfolioBaseName];
    }

    // Then try the auto portfolio name
    const autoPortfolioName = portfolioBaseName + "_auto";
    if (wealthPortfolios[autoPortfolioName]) {
      return wealthPortfolios[autoPortfolioName];
    }

    // If neither is found, use the initial portfolio structure
    return init_portfolio;
  }, [wealthPortfolios, portfolioBaseName]);

  const sparklineData = useMemo(() => {
    return (
      getPortfolioData?.sparkline?.map((el) => ({
        x: moment(el.created_at).valueOf(),
        y: Number(el.twr_performance).toFixed(2),
      })) || []
    );
  }, [getPortfolioData]);

  const allocations = useMemo(() => {
    return (
      getPortfolioData.allocations
        ?.sort((a, b) => b?.ticker_weightage - a?.ticker_weightage)
        ?.slice(0, 5) || []
    );
  }, [getPortfolioData]);

  const growth = useMemo(() => {
    const yearlyPerformance = getPortfolioData?.performance?.find(
      (el) => el.period === "1y",
    )?.performance;
    if (yearlyPerformance !== undefined) {
      return Number((yearlyPerformance * 100).toFixed(2));
    }
    return null; // Return null to indicate that the growth calculation cannot be performed
  }, [getPortfolioData]);

  const goPortfolioDetailPage = (type, url) => {
    if (type && url) {
      history.push(`/wealth-series/${type}/${url}${location.search}`);
    }
  };

  const updatedOptions = useMemo(() => {
    if (sparklineData?.length) {
      const isUpwardTrend =
        sparklineData.length > 1 &&
        Number(sparklineData[0].y) <
          Number(sparklineData[sparklineData.length - 1].y);
      const earliestDate = sparklineData[0].x;
      const cutoffDate =
        series === "core"
          ? moment("2025-09-25").valueOf()
          : moment("2024-12-23").valueOf();

      return {
        ...dummyOptions,
        chart: {
          ...dummyOptions.chart,
          height: 60,
        },
        tooltip: {
          ...dummyOptions.tooltip,
          x: {
            formatter: (val) => moment.utc(val).format("DD MMM YYYY HH:mm"),
          },
          y: {
            formatter: (_, { dataPointIndex }) => {
              const value = (
                Number(sparklineData[dataPointIndex].y) * 100
              ).toFixed(2);
              return `<span style='font-size: 14px;'>${value}%</span>`;
            },
            title: {
              formatter: () => "",
            },
          },
        },
        colors: [isUpwardTrend ? "#5EEECD" : "#F32228"],
        annotations: {
          xaxis: [
            {
              x: earliestDate,
              x2: cutoffDate,
              fillColor: "#ECECEC",
              opacity: 0.4,
              label: {
                borderColor: "#ECECEC",
                style: {
                  color: "#333",
                  background: "#ECECEC",
                },
                text: "Simulated data",
              },
            },
          ],
        },
      };
    }
    return dummyOptions;
  }, [sparklineData, series]);

  // Determine if we're currently viewing auto portfolios
  const isAutoView = useMemo(() => {
    return !!wealthPortfolios[portfolioBaseName + "_auto"];
  }, [wealthPortfolios, portfolioBaseName]);

  // Check subscription status based on the current view context
  const isSubscribed = useMemo(() => {
    // If we're in auto mode, check the auto subscription
    if (isAutoView) {
      return (
        portfolio.autoId && subscribedPortfolios.includes(portfolio.autoId)
      );
    }
    // Otherwise check regular subscription
    return subscribedPortfolios.includes(portfolio.id);
  }, [subscribedPortfolios, portfolio.id, portfolio.autoId, isAutoView]);

  // Determine the appropriate portfolio ID to use for subscription
  const subscriptionId = useMemo(() => {
    // If we're in auto mode, use the auto ID
    if (isAutoView) {
      return portfolio.autoId;
    }
    // Otherwise use the regular ID
    return portfolio.id;
  }, [isAutoView, portfolio.id, portfolio.autoId]);

  return (
    <div
      className="ws-portfolio-item"
      onClick={() => goPortfolioDetailPage(type, portfolio.url)}
    >
      <div
        className={`ws-portfolio-item__header ${getPortfolioData?.description ? "has-description" : ""}`}
      >
        <div className="ws-portfolio-item__header-top">
          <div
            className={`ws-portfolio-item__header-info ${type === "everest" ? "w-full" : ""}`}
          >
            <div className="ws-portfolio-item__title">
              {portfolio.name}
              <Tooltip
                placement="top"
                arrow
                title={
                  isSubscribed ? "Unsubscribe from alerts" : "Get email alerts"
                }
              >
                <IconButton
                  disabled={loadingSub}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleSubscription(subscriptionId, !isSubscribed);
                  }}
                >
                  {isSubscribed ? (
                    <MarkEmailReadIcon className="ws-portfolio-item__mail-icon" />
                  ) : (
                    <MailOutlineIcon className="ws-portfolio-item__mail-icon" />
                  )}
                </IconButton>
              </Tooltip>
            </div>
            <div className="ws-portfolio-item__performance">
              <span>Annual P/L*</span>
              {loadingPortfolio || growth === null ? (
                <BeautifulSkeleton
                  className="ws-portfolio-item__performance_value"
                  width={40}
                  height={20}
                />
              ) : (
                <span
                  className={`ws-portfolio-item__performance_value ${growth >= 0 ? "positive" : "negative"}`}
                >
                  {growth >= 0 ? "+" : ""}
                  {growth.toFixed(2)}%
                </span>
              )}
            </div>
          </div>

          {type !== "everest" && (
            <div className="ws-portfolio-item__header-sparklink">
              {loadingPortfolio ? (
                <div className="main-loading-overlay">
                  <div className="spinner"></div>
                </div>
              ) : (
                <SparkLines
                  series={[
                    {
                      data: sparklineData,
                    },
                  ]}
                  options={updatedOptions}
                  type={updatedOptions.chart.type}
                  height={updatedOptions.chart.height}
                  width={updatedOptions.chart.width}
                  className="w-full"
                />
              )}
            </div>
          )}
        </div>

        {!loadingPortfolio && getPortfolioData?.description && (
          <div className="ws-portfolio-item__description">
            {getPortfolioData.description}
          </div>
        )}
      </div>
      <div className="ws-portfolio-item__content">
        <div className="ws-portfolio-holdings-label">
          {series === "core"
            ? `Top 5 of ${lookup[portfolio.name]?.count} holdings`
            : "Top 5 holdings"}
        </div>
        {(loadingPortfolio
          ? Array.from({ length: 5 }).fill(null)
          : allocations
        )?.map((holding, index) =>
          loadingPortfolio ? (
            <div key={index} className="ws-portfolio-holding">
              <div
                className={`ws-portfolio-holding__ticker ${isBlurred ? "blurred" : ""}`}
              >
                <BeautifulSkeleton variant="circular" width={22} height={22} />
                <BeautifulSkeleton width={50} height={20} />
              </div>
              {type !== "everest" && (
                <BeautifulSkeleton
                  className="ws-portfolio-holding__price"
                  width={20}
                  height={20}
                />
              )}
              <BeautifulSkeleton
                className="ws-portfolio-holding__percentage"
                width={20}
                height={20}
              />
            </div>
          ) : (
            <div key={index} className="ws-portfolio-holding">
              <div
                className={`ws-portfolio-holding__ticker ${isBlurred ? "blurred" : ""}`}
              >
                <FallbackTickerIcon
                  src={`https://assets.tradealgo.com/stock-logos/${holding.ticker}.png`}
                  ticker={holding.ticker}
                  className="ws-portfolio-holding__icon"
                />
                {holding.ticker}
              </div>
              {type !== "everest" && (
                <div className="ws-portfolio-holding__price">
                  <CssPop comparator={holding.price} ticker={holding.ticker} />
                </div>
              )}
              <div className="ws-portfolio-holding__percentage">
                {(holding.ticker_weightage * 100)?.toFixed(2)}%
              </div>
            </div>
          ),
        )}
      </div>
    </div>
  );
};

const stateToProps = (state) => ({
  wealthPortfolios: state.wealthPortfolio,
  loadingPortfolio: state.wealthPortfolio.loading,
  loadingSub: state.portfolioSubscribe.loading,
  subscribedPortfolios: state.portfolioSubscribe.subscribedPortfolios,
});

const dispatchToProps = (dispatch) => ({
  handleSubscription: (id, subscribe) =>
    dispatch(portfolioSubscriptionRequest(id, subscribe)),
});

export default connect(stateToProps, dispatchToProps)(WSPortfolioItem);
